// {CC 08/02/2026} Minimal Website Demo Viewer - Simple audio pipeline with hardcoded system prompt
import React, { useState, useRef } from 'react';
import { Box, Typography, Container, Alert } from '@mui/material';

const WEBSOCKET_URL = 'wss://smartoperator-prod-main-api-103200896563.europe-west4.run.app/website';
const RECEIVE_SAMPLE_RATE = 24000;
const SEND_SAMPLE_RATE = 16000;

const WebsiteDemoViewer = () => {
  const [sessionActive, setSessionActive] = useState(false);
  const [status, setStatus] = useState('disconnected');
  const [micPermission, setMicPermission] = useState(null);
  const websocketRef = useRef(null);
  const micStreamRef = useRef(null);
  const sendAudioContextRef = useRef(null);
  const receiveAudioContextRef = useRef(null);
  const workletNodeRef = useRef(null);
  const audioQueueRef = useRef([]);
  const nextPlayTimeRef = useRef(0);
  const isPlayingRef = useRef(false);
  const activeSourcesRef = useRef([]);

  const processAudioQueue = () => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      return;
    }
    const arrayBuffer = audioQueueRef.current.shift();
    const pcm_i16 = new Int16Array(arrayBuffer);
    const pcm_f32 = new Float32Array(pcm_i16.length);
    for (let i = 0; i < pcm_f32.length; i++) {
      pcm_f32[i] = pcm_i16[i] / 32768.0;
    }
    const audioBuffer = receiveAudioContextRef.current.createBuffer(
      1,
      pcm_f32.length,
      RECEIVE_SAMPLE_RATE
    );
    audioBuffer.copyToChannel(pcm_f32, 0);
    const source = receiveAudioContextRef.current.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(receiveAudioContextRef.current.destination);
    activeSourcesRef.current.push(source);
    const currentTime = receiveAudioContextRef.current.currentTime;
    const scheduledTime = Math.max(currentTime + 0.010, nextPlayTimeRef.current);
    source.start(scheduledTime);
    nextPlayTimeRef.current = scheduledTime + audioBuffer.duration;
    source.onended = () => {
      try {
        source.disconnect();
      } catch (_) {}
      const idx = activeSourcesRef.current.indexOf(source);
      if (idx !== -1) activeSourcesRef.current.splice(idx, 1);
    };
    if (audioQueueRef.current.length > 0) {
      setTimeout(() => processAudioQueue(), 0);
    } else {
      isPlayingRef.current = false;
    }
  };

  const setupMicAndWorklet = async () => {
    try {
      sendAudioContextRef.current = new AudioContext({ sampleRate: SEND_SAMPLE_RATE });
      console.log(`[DEMO] Send AudioContext: ${sendAudioContextRef.current.sampleRate} Hz`);
      if (sendAudioContextRef.current.state === 'suspended') {
        await sendAudioContextRef.current.resume();
      }
      const source = sendAudioContextRef.current.createMediaStreamSource(micStreamRef.current);
      await sendAudioContextRef.current.audioWorklet.addModule(process.env.PUBLIC_URL + '/pcm16-processor.js');
      workletNodeRef.current = new AudioWorkletNode(
        sendAudioContextRef.current,
        'pcm16-processor'
      );
      workletNodeRef.current.port.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer && websocketRef.current?.readyState === WebSocket.OPEN) {
          const audioData = new Uint8Array(event.data);
          const header = new Uint8Array([65]);
          const combined = new Uint8Array(header.length + audioData.length);
          combined.set(header);
          combined.set(audioData, header.length);
          websocketRef.current.send(combined.buffer);
        }
      };
      source.connect(workletNodeRef.current);
      const zeroGain = sendAudioContextRef.current.createGain();
      zeroGain.gain.value = 0;
      workletNodeRef.current.connect(zeroGain);
      zeroGain.connect(sendAudioContextRef.current.destination);
      console.log('[DEMO] Microphone ready');
    } catch (error) {
      console.error('[DEMO] Mic setup error:', error);
      setStatus('error');
      stopSession();
    }
  };

  const startSession = async () => {
    if (sessionActive) return;
    try {
      setStatus('requesting_permissions');
      micStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      setMicPermission('granted');
      console.log('[DEMO] Microphone granted');
      setStatus('connecting');
      receiveAudioContextRef.current = new AudioContext({
        sampleRate: RECEIVE_SAMPLE_RATE,
        latencyHint: 'interactive',
      });
      if (receiveAudioContextRef.current.state === 'suspended') {
        await receiveAudioContextRef.current.resume();
      }
      nextPlayTimeRef.current = receiveAudioContextRef.current.currentTime;
      audioQueueRef.current = [];
      console.log(`[DEMO] Receive AudioContext: ${receiveAudioContextRef.current.sampleRate} Hz`);
      // {CC 08/02/2026} Connect directly to /website - no config parameter!
      websocketRef.current = new WebSocket(WEBSOCKET_URL);
      websocketRef.current.binaryType = 'arraybuffer';
      websocketRef.current.onopen = async () => {
        setStatus('connected');
        await setupMicAndWorklet();
        setSessionActive(true);
      };
      websocketRef.current.onmessage = (event) => {
        if (typeof event.data === 'string') {
          try {
            const message = JSON.parse(event.data);
            if (message.type === 'generation_interrupted') {
              console.log('[DEMO] Interrupted - clearing audio queue');
              activeSourcesRef.current.forEach((src) => {
                try { src.stop(); } catch (_) {}
                try { src.disconnect(); } catch (_) {}
              });
              activeSourcesRef.current = [];
              audioQueueRef.current = [];
              isPlayingRef.current = false;
              nextPlayTimeRef.current = receiveAudioContextRef.current.currentTime + 0.010;
            }
          } catch (e) {}
        }
        else if (event.data instanceof ArrayBuffer) {
          audioQueueRef.current.push(event.data);
          if (!isPlayingRef.current) {
            isPlayingRef.current = true;
            processAudioQueue();
          }
        }
      };
      websocketRef.current.onclose = () => {
        console.log('[DEMO] WebSocket closed');
        stopSession();
      };
      websocketRef.current.onerror = (error) => {
        console.error('[DEMO] WebSocket error:', error);
        setStatus('error');
        stopSession();
      };
    } catch (error) {
      console.error('[DEMO] Error starting session:', error);
      if (error.name === 'NotAllowedError') {
        setMicPermission('denied');
        setStatus('permission_denied');
      } else {
        setStatus('error');
      }
      stopSession();
    }
  };

  const stopSession = () => {
    activeSourcesRef.current.forEach((src) => {
      try { src.stop(); } catch (_) {}
      try { src.disconnect(); } catch (_) {}
    });
    activeSourcesRef.current = [];
    if (websocketRef.current) {
      if (websocketRef.current.readyState === WebSocket.OPEN) {
        websocketRef.current.close();
      }
      websocketRef.current = null;
    }
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (sendAudioContextRef.current) {
      sendAudioContextRef.current.close();
      sendAudioContextRef.current = null;
    }
    if (receiveAudioContextRef.current) {
      receiveAudioContextRef.current.close();
      receiveAudioContextRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    setSessionActive(false);
    setStatus('disconnected');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#faf6f3',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3,
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center' }}>
          {!sessionActive && status !== 'permission_denied' && status !== 'error' && (
            <Box mb={4}>
              <Typography variant="body1" color="textSecondary" mb={2}>
                Click the button below to start your session.
                <br />
                Make sure your microphone is enabled.
              </Typography>
            </Box>
          )}
          {micPermission === 'denied' && (
            <Box mb={4}>
              <Alert severity="warning">
                Please enable microphone access in your browser settings and refresh the page.
              </Alert>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box
              component="img"
              src={
                sessionActive
                  ? 'https://cdn.prod.website-files.com/68b6e14e341c572968c12c24/692e0d1674d5cbf1e856d45b_Gemini_Generated_Image_40uuu840uuu840uu.png'
                  : 'https://cdn.prod.website-files.com/68b6e14e341c572968c12c24/692e0d164fa892eb0c815251_Gemini_Generated_Image_1g65rj1g65rj1g65.png'
              }
              alt={sessionActive ? 'End Demo' : 'Start Demo'}
              onClick={
                status === 'connecting' || status === 'requesting_permissions'
                  ? undefined
                  : sessionActive
                  ? stopSession
                  : startSession
              }
              sx={{
                maxWidth: '100%',
                width: { xs: '100px', sm: '125px', md: '150px' },
                height: 'auto',
                borderRadius: '20px',
                border: '2px solid #6e7b60',
                cursor:
                  status === 'connecting' || status === 'requesting_permissions'
                    ? 'wait'
                    : 'pointer',
                opacity:
                  status === 'connecting' || status === 'requesting_permissions'
                    ? 0.6
                    : 1,
                transition: 'opacity 0.2s, transform 0.1s',
                '&:hover': {
                  transform:
                    status === 'connecting' || status === 'requesting_permissions'
                      ? 'none'
                      : 'scale(1.05)',
                },
                '&:active': {
                  transform:
                    status === 'connecting' || status === 'requesting_permissions'
                      ? 'none'
                      : 'scale(0.98)',
                },
              }}
            />
          </Box>
          {(status === 'connecting' || status === 'requesting_permissions') && (
            <Box mt={3}>
              <Typography variant="body2" color="textSecondary">
                {status === 'requesting_permissions' ? 'Requesting microphone access...' : 'Connecting...'}
              </Typography>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default WebsiteDemoViewer;
